import { useMemo, useState } from "react";
import { Background, Controls, MiniMap, ReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { AlertTriangle, Eye, EyeOff } from "lucide-react";
import { buildRunMap } from "../runMap";
import { formatDuration } from "../executionTimeline";
import RunMapNode from "./RunMapNode";

// El grafo de la versión que corrió, con lo que pasó encima. Por defecto
// muestra solo los pasos que corrieron: en una conversación de 77 pasos
// corrieron 31, y los otros 46 no son lo que pasó.

const nodeTypes = { runStep: RunMapNode };

const MINIMAP_TONE = (node) => {
  if (!node.data?.ran) return "#3f3f46";
  if (node.data.lastStatus === "failed") return "#f43f5e";
  return "#10b981";
};

const PassList = ({ node, onClose }) => (
  <div className="absolute right-3 top-3 z-10 w-64 rounded-xl border border-white/10 bg-zinc-950/95 p-3 shadow-xl backdrop-blur">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="truncate text-xs font-medium text-white">{node.data.name}</div>
        <div className="truncate text-[10px] text-gray-500">{node.data.stepType}</div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="text-[10px] text-gray-500 hover:text-white"
      >
        close
      </button>
    </div>

    {node.data.ran ? (
      <>
        <div className="mt-2 flex items-center gap-3 text-[10px] text-gray-400">
          <span>
            {node.data.runs} {node.data.runs === 1 ? "run" : "runs"}
          </span>
          <span>{formatDuration(node.data.totalMs)} total</span>
          {node.data.coins > 0 && <span className="text-amber-300/80">{node.data.coins} coins</span>}
        </div>
        <div className="mt-2 max-h-48 space-y-1 overflow-auto border-t border-white/5 pt-2">
          {node.data.passes.map((pass) => (
            <div key={pass.seq} className="flex items-center justify-between text-[10px]">
              <span className="font-mono text-gray-600">#{pass.seq}</span>
              <span className="text-gray-500">pass {pass.pass}</span>
              <span className="text-gray-300">{formatDuration(pass.durationMs)}</span>
              <span className={pass.status === "failed" ? "text-rose-400" : "text-gray-500"}>
                {pass.status}
              </span>
            </div>
          ))}
        </div>
      </>
    ) : (
      <p className="mt-2 text-[10px] leading-relaxed text-gray-500">
        {node.data.skippedReason === "not_taken"
          ? `The condition “${node.data.decidedBy || "?"}” went down another branch, so this step was left out.`
          : "The run ended without ever reaching this step."}
      </p>
    )}
  </div>
);

export default function RunMapView({ graph, timeline, notExecuted, graphError }) {
  const [showSkipped, setShowSkipped] = useState(false);
  const [selected, setSelected] = useState(null);

  const map = useMemo(
    () => buildRunMap({ graph, timeline, notExecuted, showSkipped }),
    [graph, timeline, notExecuted, showSkipped]
  );

  const edges = useMemo(
    () =>
      map.edges.map((edge) => ({
        ...edge,
        type: "smoothstep",
        animated: false,
        style: { stroke: "rgba(255,255,255,0.15)", strokeWidth: 1.5 },
        label: edge.branch === "false" ? "false" : edge.branch === "true" ? "true" : undefined,
        labelStyle: { fill: "#71717a", fontSize: 9 },
        labelBgStyle: { fill: "#09090b" },
      })),
    [map.edges]
  );

  if (graphError) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2">
        <AlertTriangle className="h-6 w-6 text-amber-400" />
        <div className="text-xs text-amber-300">Could not load the graph this run used</div>
        <p className="text-[10px] text-gray-500">{graphError}</p>
      </div>
    );
  }

  if (!graph) {
    return (
      <div className="flex h-40 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-white">
            {map.shown} of {map.total} steps
          </h3>
          <p className="text-[10px] text-gray-500">
            {showSkipped
              ? "Dashed steps never ran — they are here for context."
              : `Only what ran. ${map.hidden} steps of the graph are hidden.`}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowSkipped((current) => !current)}
          className="flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1.5 text-[11px] font-medium text-gray-300 transition-colors hover:border-violet-500/30 hover:text-white"
        >
          {showSkipped ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {showSkipped ? "Only what ran" : `Show the ${map.hidden} that didn't run`}
        </button>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl border border-white/10 bg-zinc-950/60">
        <ReactFlow
          nodes={map.nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodeClick={(_, node) => setSelected(node)}
          onPaneClick={() => setSelected(null)}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          fitView
          fitViewOptions={{ padding: 0.15 }}
          minZoom={0.08}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#27272a" gap={20} />
          <Controls showInteractive={false} className="!bg-zinc-900 !border-white/10" />
          <MiniMap
            nodeColor={MINIMAP_TONE}
            maskColor="rgba(0,0,0,0.7)"
            className="!bg-zinc-900 !border !border-white/10"
            pannable
            zoomable
          />
        </ReactFlow>

        {selected && <PassList node={selected} onClose={() => setSelected(null)} />}
      </div>

      {/* Se sabe qué nodos corrieron, no por cuál arista se pasó de uno al
          siguiente: eso no se registra. Decirlo evita leer las flechas como
          el camino que tomó la ejecución. */}
      <p className="text-[10px] text-gray-600">
        Arrows are the graph's own connections, not a recorded path — the order things ran in is
        the timeline's.
      </p>
    </div>
  );
}
